import React from "react";
import { Plus } from "lucide-react";
import { useAutoStackPurchases } from "../hooks/query/queries";
import transactions from "../assets/icons/transactions.svg";
import carouselItem2 from "../assets/images/autostack/carousel_item_2.svg";
import { Button, Typography } from "./ui";
import { useNavigate } from "react-router-dom";
import CurrencyFormatter from "../services/currencyFormatterService";
import AutoStackTransactionHistorySkeleton from "./skeletons/AutoStackTransactionHistorySkeleton";

interface AutoStackTransactionHistoryProps {
  stackId: string;
}
interface Purchase {
  _id: string;
  user_id: string;
  user_autostack: string;
  scheduled_purchase_date: string;
  start_date: string;
  amount_fiat: number;
  amount_asset: number;
  status: "scheduled" | "pending" | "completed" | "failed" | "cancelled";
  type: string;
  attempts: number;
  max_attempts: number;
  createdAt: string;
  updatedAt: string;
  funding_type?: string;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case "completed":
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-green-700">
          SUCCESS
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-red-700">
          FAILED - LOW WALLET BALANCE
        </span>
      );
    case "scheduled":
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-blue-700">
          SCHEDULED
        </span>
      );
    case "pending":
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-yellow-700">
          PENDING
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-gray-700">
          CANCELLED
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center font-semibold py-1 text-xs max-w-36 text-gray-700">
          {status.toUpperCase()}
        </span>
      );
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AutoStackTransactionHistory: React.FC<
  AutoStackTransactionHistoryProps
> = ({ stackId }) => {
  const {
    data: purchasesData,
    isLoading,
    isFetching,
    error,
  } = useAutoStackPurchases(stackId);
  const navigate = useNavigate();
  const currencyFormatter = new CurrencyFormatter({ locale: "en-ZA" });

  if (isLoading || isFetching) {
    return <AutoStackTransactionHistorySkeleton />;
  }

  const purchaseFundingType = (purchase: Purchase) => {
    if (!purchase.funding_type) {
      return "Unknown";
    }

    if (purchase.funding_type === "bank_transfer") {
      return "Bank transfer";
    } else {
      return "Wallet";
    }
  };

  const purchases = (purchasesData?.results?.filter(
    (purchase: Purchase) => purchase.status !== "scheduled"
  ) || []) as Purchase[];

  if (error ?? purchases.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-bg-section w-full">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">
          Transaction history
        </h2>
        <div className="flex flex-col items-center justify-center gap-2 h-auto md:h-[calc(100%-100px)]">
          <img
            src={carouselItem2}
            alt="transactions"
            className="w-42 md:w-72"
          />
          <Typography variant="h3" className="text-gray-800">
            {error
              ? "Failed to load transaction history"
              : "No transactions yet"}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-bg-section w-full max-h-[calc(100vh-140px)] overflow-y-auto">
      <div className="flex items-center justify-between gap-2 mb-6">
        <h2 className="text-base md:text-xl font-semibold text-text-secondary">
          Transaction history
        </h2>
        <Button
          onClick={() => navigate(`/autostack/stackmore/${stackId}`)}
          className="bg-transparent hover:bg-gray-50 border border-bg-section rounded-full flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4 text-text-primary" />
          <Typography
            variant="span"
            className="text-xs md:text-sm text-text-primary"
          >
            Stack more
          </Typography>
        </Button>
      </div>

      {purchases.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm md:text-base text-text-secondary">
            No transactions yet
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {purchases.map((purchase: Purchase) => (
            <div
              key={purchase._id}
              className="flex items-center justify-between border border-bg-section p-4 hover:bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-accent-pink flex items-center justify-center flex-shrink-0">
                  <img src={transactions} alt="transactions" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs md:text-sm text-text-primary">
                      {purchaseFundingType(purchase)}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-text-secondary">
                    {formatDate(purchase.scheduled_purchase_date)}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <div className="flex flex-col">
                  <span className="font-semibold text-xs md:text-sm text-text-primary">
                    {currencyFormatter.format(
                      purchase.amount_fiat,
                      "fiat",
                      "ZAR"
                    )}
                  </span>
                </div>
                {getStatusBadge(purchase.status)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoStackTransactionHistory;
